/**
 * Rock Off Retail - USF instant-search popup helper (2 Oct 2026, rev 3: close).
 *
 * Pairs with assets/rockoff-usf-search.css. The popup is rendered by the USF
 * app (Vue), so this script NEVER edits USF text nodes and never inserts
 * children into USF's own nodes. It only writes attributes and one chip row:
 *   1. on each scan it reads the title button's current textContent (always the
 *      true title), works out the design name + garment and writes them as
 *      data-ro-name (on the button) and data-ro-garment (on
 *      .usf-is-content-wrapper). The CSS hides the button text only while
 *      [data-ro-name] is present and draws the name with ::before;
 *   2. marks the popup root data-ro-state="trending" | "query", read from the
 *      input that opened the popup (focusin tracking);
 *   3. sets --ro-pop-max on the popup (space below its own top edge) so the CSS
 *      can cap the whole popup; written only when it changes;
 *   4. on the Trending state adds a "Popular" chip row (JSON script
 *      #ro-usf-popular-data from snippets/usf.liquid, parsed once) as the first
 *      child of .usf-popup (after USF's own input box on mobile); never
 *      duplicated, re-inserted if USF removes it;
 *   5. closing (9 Oct 2026): (a) Escape (document keydown, capture) closes a
 *      VISIBLE popup through USF's own usf.utils.hideInstantSearch(input), the
 *      call USF makes itself after a form submit; it does nothing and does not
 *      swallow the key when no popup is visible (cart drawer, menus keep
 *      Escape). When it did close a popup and the key came from a search field,
 *      preventDefault() stops the browser clearing the type=search query, so
 *      Escape closes and keeps the query; a second Escape (no popup open)
 *      behaves natively. Never stopPropagation. Focus is not moved, so it stays
 *      on the header input. (b) DESKTOP ONLY: one <button class="ro-usf-close">
 *      is added as the LAST direct child of .usf-popup (same pattern as the
 *      chip row; never duplicated, re-inserted if USF removes it; last so it
 *      cannot disturb the chip row's position check). On body.usf-mobile USF
 *      draws its own .usf-close, so any button of ours is removed and none is
 *      added (Escape still works). mousedown is prevented so the button never
 *      takes focus or blurs the input; mousedown and click do not propagate to
 *      USF's document handlers; click closes exactly like Escape and does not
 *      refocus anything (focusing the input would fire USF's show handler
 *      again). Label = window._roSearchCloseLabel (snippets/usf.liquid),
 *      English fallback. (c) Fallback: if the popup is still visible on the
 *      next animation frame (or usf.utils is missing) display:none is set on
 *      the popup root, unless a search field got focus/input since the close
 *      (generation counter), and that forced hide is lifted on the next
 *      focus/input of a search field, because USF's own show may not restyle a
 *      root it believes is already visible.
 * Scans are coalesced with requestAnimationFrame; mutations outside any
 * .usf-popup are ignored. Everything is in try/catch: it can never break search.
 */
(function () {
  'use strict';

  if (window.__roUsfSearch) return;
  window.__roUsfSearch = true;

  var POPUP = '.usf-popup.usf-is';
  var CHIPS_CLASS = 'ro-usf-chips';
  var SEP_RE = /\s+[-–—]\s+/;
  /* The WHOLE suffix must be garment words ("Kids T-Shirt", "Ladies Long Sleeve Tee"),
     so a design suffix like "Top Of The World" is never taken for a garment. */
  var GARMENT_WORD = '(?:t-shirt|tee|shirt|hoodie|sweatshirt|sweat|vest|tank|top|long|sleeve|raglan|baseball|bodysuit|babygrow|onesie|kids|kid|ladies|womens|women|mens|men|unisex|baby|toddler|organic|backpack|bag|tote|cap|beanie|hat|mug|socks|patch|poster|jacket|dress|leggings|zip|pullover|crop|fitted)s?';
  var GARMENT_RE = new RegExp('^' + GARMENT_WORD + '(?:[\\s/&]+' + GARMENT_WORD + ')*$', 'i');
  /* "SPIRAL - X" and "SPIRAL-X": the brand prefix is stripped before splitting */
  var PREFIX_RE = /^spiral\s*[-–—]\s*/i;
  var QSEL = 'input[name="q"], .usf-is-inputbox input';
  var scheduled = false;
  var lastInput = null;
  var popular;
  var forcedHidden = [];
  var CLOSE_CLASS = 'ro-usf-close';
  var gen = 0; /* bumped on focusin/input of a search field; see closePopup */

  function parseTitle(full, vendor) {
    var s = (full || '').replace(/\s+/g, ' ').trim();
    var stripped = s.replace(PREFIX_RE, '');
    var hadPrefix = stripped !== s;
    s = stripped || s;
    var parts = s.split(SEP_RE).filter(function (p) { return p; });
    var garment = '';
    if (parts.length > 1 && GARMENT_RE.test(parts[parts.length - 1].trim())) garment = parts.pop();
    if (!hadPrefix && parts.length > 1) {
      var first = parts[0].toLowerCase();
      if (first === 'spiral' || (vendor && first === vendor.toLowerCase())) parts.shift();
    }
    return { name: parts.join(' - ') || s, garment: garment };
  }

  function setAttr(el, name, value) {
    if (value === '' || value == null) {
      if (el.hasAttribute(name)) el.removeAttribute(name);
    } else if (el.getAttribute(name) !== value) {
      el.setAttribute(name, value);
    }
  }

  function queryValue(popup) {
    var own = popup.querySelector('.usf-is-inputbox input');
    if (own && own.offsetParent !== null) return (own.value || '').trim();
    var inp = lastInput && lastInput.isConnected ? lastInput : null;
    if (!inp) {
      var a = document.activeElement;
      if (a && a.matches && a.matches(QSEL)) inp = a;
    }
    return inp ? (inp.value || '').trim() : '';
  }

  function popularData() {
    if (popular !== undefined) return popular;
    popular = null;
    try {
      var el = document.getElementById('ro-usf-popular-data');
      if (el) {
        var d = JSON.parse(el.textContent || '{}');
        if (d && d.items && d.items.length) popular = d;
      }
    } catch (e) { popular = null; }
    return popular;
  }

  function buildRow(data) {
    var row = document.createElement('div');
    row.className = CHIPS_CLASS;
    var lab = document.createElement('span');
    lab.className = CHIPS_CLASS + '__label';
    lab.textContent = data.label || 'Popular';
    row.appendChild(lab);
    for (var i = 0; i < data.items.length; i++) {
      var it = data.items[i];
      if (!it || !it.u || !it.t) continue;
      var a = document.createElement('a');
      a.className = CHIPS_CLASS + '__chip';
      a.href = it.u;
      a.textContent = it.t;
      row.appendChild(a);
    }
    return row;
  }

  function syncChips(popup, trending) {
    var all = popup.querySelectorAll('.' + CHIPS_CLASS);
    var data = trending ? popularData() : null;
    var i;
    if (!data) {
      for (i = 0; i < all.length; i++) all[i].parentNode.removeChild(all[i]);
      return;
    }
    for (i = 1; i < all.length; i++) all[i].parentNode.removeChild(all[i]);
    /* Mobile: USF wraps .usf-is-inputbox in an unclassed div, so anchor on the
       popup CHILD that contains the input box and put the chips right after it. */
    var inbox = popup.querySelector('.usf-is-inputbox');
    while (inbox && inbox.parentNode !== popup) inbox = inbox.parentNode;
    var row = all[0];
    if (row && row.parentNode === popup && row.previousElementSibling === inbox) return;
    if (!row) row = buildRow(data);
    popup.insertBefore(row, inbox ? inbox.nextSibling : popup.firstChild);
  }

  function buildClose() {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = CLOSE_CLASS;
    btn.setAttribute('aria-label', window._roSearchCloseLabel || 'Close');
    var NS = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS(NS, 'path');
    path.setAttribute('d', 'M5 5l14 14M19 5L5 19');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '2.5');
    path.setAttribute('stroke-linecap', 'square');
    svg.appendChild(path);
    btn.appendChild(svg);
    /* Keep the input's focus/blur exactly as it was; the click does the work. */
    btn.addEventListener('mousedown', function (e) { e.preventDefault(); e.stopPropagation(); });
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      closePopup();
    });
    return btn;
  }

  function syncClose(popup) {
    var all = popup.querySelectorAll('.' + CLOSE_CLASS);
    var i;
    /* Mobile: USF draws its own .usf-close in the input row; ours would be a second X. */
    if (document.body && document.body.classList.contains('usf-mobile')) {
      for (i = 0; i < all.length; i++) all[i].parentNode.removeChild(all[i]);
      return;
    }
    for (i = 1; i < all.length; i++) all[i].parentNode.removeChild(all[i]);
    var btn = all[0];
    if (btn && btn.parentNode === popup && btn === popup.lastElementChild) return;
    if (!btn) btn = buildClose();
    popup.appendChild(btn);
    /* The button is absolutely positioned against the popup root. */
    if (window.getComputedStyle(popup).position === 'static') popup.style.position = 'relative';
  }

  function isShown(popup) {
    /* getClientRects() is empty when the popup or any ancestor is display:none */
    return popup.style.display !== 'none' && popup.getClientRects().length > 0;
  }

  function visiblePopups() {
    var out = [];
    var popups = document.querySelectorAll(POPUP);
    for (var i = 0; i < popups.length; i++) if (isShown(popups[i])) out.push(popups[i]);
    return out;
  }

  function closeInput(popup) {
    if (lastInput && lastInput.isConnected) return lastInput;
    var a = document.activeElement;
    if (a && a.matches && a.matches(QSEL)) return a;
    return popup.querySelector('.usf-is-inputbox input') || document.querySelector(QSEL);
  }

  function closePopup() {
    try {
      var open = visiblePopups();
      if (!open.length) return false;
      var input = closeInput(open[0]);
      var g = gen;
      try {
        if (window.usf && usf.utils && typeof usf.utils.hideInstantSearch === 'function') usf.utils.hideInstantSearch(input);
      } catch (e) { /* fall through to the fallback below */ }
      /* Fallback: USF normally hides (inline display:none) by the next frame.
         If it did not, or usf.utils is missing, hide the root ourselves. */
      (window.requestAnimationFrame || window.setTimeout)(function () {
        try {
          if (g !== gen) return; /* a search field was used since the close; USF owns the state now */
          var still = visiblePopups();
          for (var i = 0; i < still.length; i++) {
            still[i].style.display = 'none';
            if (forcedHidden.indexOf(still[i]) < 0) forcedHidden.push(still[i]);
          }
        } catch (e) { /* never break search */ }
      });
      return true;
    } catch (e) { return false; }
  }

  /* A forced display:none (fallback above) is lifted when a search field gets
     focus or input, before USF's own show handler runs (capture phase). */
  function liftForcedHide() {
    while (forcedHidden.length) {
      var p = forcedHidden.pop();
      if (p && p.style && p.style.display === 'none') p.style.display = '';
    }
  }

  function capHeight(popup) {
    var top = popup.getBoundingClientRect().top;
    var v = Math.max(280, Math.round(window.innerHeight - top - 12)) + 'px';
    if (popup.style.getPropertyValue('--ro-pop-max') !== v) popup.style.setProperty('--ro-pop-max', v);
  }

  function scan() {
    scheduled = false;
    try {
      var popups = document.querySelectorAll(POPUP);
      for (var i = 0; i < popups.length; i++) {
        var popup = popups[i];
        var titles = popup.querySelectorAll('.usf-is-product .usf-title');
        for (var t = 0; t < titles.length; t++) {
          var btn = titles[t];
          var wrap = btn.closest('.usf-is-content-wrapper');
          var vEl = wrap && wrap.querySelector('.usf-vendor');
          var r = parseTitle(btn.textContent, vEl ? vEl.textContent.trim() : '');
          setAttr(btn, 'data-ro-name', r.name);
          if (wrap) setAttr(wrap, 'data-ro-garment', r.garment);
        }

        var trending = !popup.querySelector('.usf-is-side') &&
          !!popup.querySelector('.usf-is-products') &&
          !popup.querySelector('.usf-is-did-you-mean') &&
          queryValue(popup) === '';
        setAttr(popup, 'data-ro-state', trending ? 'trending' : 'query');
        syncChips(popup, trending);
        syncClose(popup);
        capHeight(popup);
      }
    } catch (e) { /* never break search */ }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    (window.requestAnimationFrame || window.setTimeout)(scan);
  }

  function relevant(records) {
    for (var i = 0; i < records.length; i++) {
      var r = records[i];
      var t = r.target;
      var el = t.nodeType === 1 ? t : t.parentElement;
      if (el && el.closest && el.closest('.usf-popup')) return true;
      if (r.type === 'childList') {
        for (var j = 0; j < r.addedNodes.length; j++) {
          var n = r.addedNodes[j];
          if (n.nodeType === 1 && ((n.matches && n.matches('.usf-popup')) || (n.querySelector && n.querySelector('.usf-popup')))) return true;
        }
      }
    }
    return false;
  }

  function init() {
    if (typeof MutationObserver === 'undefined' || !document.body) return;
    /* characterData is kept only because Vue may patch a title's text node in
       place; we no longer write text, so there is no feedback loop. */
    new MutationObserver(function (records) {
      if (relevant(records)) schedule();
    }).observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style'] });
    document.addEventListener('focusin', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches(QSEL)) { lastInput = t; gen++; liftForcedHide(); schedule(); }
    }, true);
    document.addEventListener('input', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches(QSEL)) { lastInput = t; gen++; liftForcedHide(); schedule(); }
    }, true);
    /* Escape closes a visible popup only; otherwise the key is left alone. When
       it closed one from a search field, preventDefault keeps the browser from
       clearing the type=search query (a second Escape then acts natively).
       Never stopPropagation, so other Escape handlers still run. */
    document.addEventListener('keydown', function (e) {
      try {
        if (e.key !== 'Escape' && e.key !== 'Esc') return;
        if (e.isComposing) return;
        var t = e.target;
        if (closePopup() && t && t.matches && t.matches(QSEL)) e.preventDefault();
      } catch (err) { /* never break search */ }
    }, true);
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('scroll', function () {
      if (document.querySelector(POPUP)) schedule();
    }, { passive: true });
    schedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
